import type { PostStatus, TargetStatus } from '@postpilot/core/domain';
import {
  AlertTriangle,
  CalendarClock,
  Check,
  CircleSlash,
  FileText,
  Loader2,
  X,
} from 'lucide-react';
import type * as React from 'react';

/**
 * How every status is presented. Defined once, here.
 *
 * Split out of status-badge.tsx so that file exports components only (React Fast
 * Refresh cannot hot-swap a module that mixes components with plain functions).
 *
 * Each status gets a tinted background, a matching border, AND an icon. The icon
 * is not decoration: colour alone fails for the ~8% of men with a colour-vision
 * deficiency, and red/green — the two statuses it matters most to tell apart —
 * is precisely the pair they cannot distinguish. The shape carries the meaning
 * too.
 *
 * `label` is the user-facing wording, deliberately not our internal vocabulary.
 * A user does not know what a "target" is, and "partial" means nothing until you
 * say what was partial about it.
 */
export const STATUS_CONFIG: Record<
  PostStatus | TargetStatus,
  { label: string; icon: React.ComponentType<{ className?: string }>; className: string }
> = {
  draft: {
    label: 'Draft',
    icon: FileText,
    className: 'border-status-draft/25 bg-status-draft/10 text-status-draft',
  },
  scheduled: {
    label: 'Scheduled',
    icon: CalendarClock,
    className: 'border-status-scheduled/25 bg-status-scheduled/10 text-status-scheduled',
  },
  processing: {
    label: 'Publishing',
    icon: Loader2,
    className: 'border-status-processing/25 bg-status-processing/10 text-status-processing',
  },
  published: {
    label: 'Published',
    icon: Check,
    className: 'border-status-published/25 bg-status-published/10 text-status-published',
  },
  partial: {
    label: 'Partly sent',
    icon: AlertTriangle,
    className: 'border-status-partial/25 bg-status-partial/10 text-status-partial',
  },
  failed: {
    label: 'Failed',
    icon: X,
    className: 'border-status-failed/25 bg-status-failed/10 text-status-failed',
  },
  cancelled: {
    label: 'Cancelled',
    icon: CircleSlash,
    className: 'border-status-draft/25 bg-status-draft/10 text-status-draft',
  },
  pending: {
    label: 'Queued',
    icon: CalendarClock,
    className: 'border-status-scheduled/25 bg-status-scheduled/10 text-status-scheduled',
  },
};

/** The plain-English label, for places a badge would be too heavy (tooltips, aria-labels). */
export function statusLabel(status: PostStatus | TargetStatus): string {
  return STATUS_CONFIG[status].label;
}
